import React, { FC, useCallback, useMemo, useState } from 'react'

import { Header, Loader, ErrorHandler } from 'components'
import { Routes } from 'common/constants'
import { logBridgeError, sendSelectContent } from 'common/helpers'
import { useScreenFinishLoadTac } from 'common/hooks/useScreenFinishLoadTac'
import { Page, PageContent, Divider, makeTraceDetails } from 'common'
import { useAppContext } from 'contexts/app-context/hooks/useAppContext'
import { PrivacyPolicyLink } from 'components/privacy-policy-link'
import { isEmpty } from 'lodash'
import { useLocation } from 'react-router-dom'

import { TabDefinition, TabPager } from '@interco/inter-ui/components/TabPager'

import { ReportsAvailable } from './reports-available/ReportsAvailable'
import { ReportsRequested } from './reports-requested/ReportsRequested'
import {
  TabItemStyle,
  TabPanelStyle,
  StyledTabPager,
  MainContainerStyle,
  BottomCard,
} from './styles'
import { REPORT_HISTORY_TABS_ID } from './utils/mappers'

export const ReportsHistory: FC = () => {
  const location = useLocation()
  const { state } = location

  const goToRequestedReports = !!state?.goToRequestedReports

  const initalTab = goToRequestedReports
    ? REPORT_HISTORY_TABS_ID.REQUESTED
    : REPORT_HISTORY_TABS_ID.AVAILABLE

  const { isAppLoading, isAppError, isAppActive, privacyCenterContent, userSelectedEntity } =
    useAppContext()

  const [activeTab, setActiveTab] = useState<string | number>(initalTab)

  const onTabSelectHandler = useCallback(
    async (tabId: string | number) => {
      try {
        await sendSelectContent({
          flow: 'reports_history',
          step: 'reports_history',
          screen_name: Routes.REPORTS_HISTORY,
          element_name: 'switchReportHistoryTab',
          element_type: 'button',
          parent_name: 'ReportsHistory',
          scenario: JSON.stringify({
            screen: Routes.REPORTS_HISTORY,
            product: userSelectedEntity,
            tabId: String(tabId),
            ref_type: 'button',
            content_action: 'touch',
          }),
        })
      } catch (error: unknown) {
        logBridgeError('send reports history tab tic', error)
      }

      setActiveTab(tabId)
    },
    [userSelectedEntity],
  )

  const content = privacyCenterContent?.components?.reportsHistory?.content

  const isActive = privacyCenterContent?.components?.reportsHistory?.isActive

  const tabs: TabDefinition[] = useMemo(
    () => [
      {
        tabId: REPORT_HISTORY_TABS_ID.AVAILABLE,
        label: content?.availableReports?.title ?? '',
        content: <ReportsAvailable />,
      },
      {
        tabId: REPORT_HISTORY_TABS_ID.REQUESTED,
        label: content?.requestedReports?.title ?? '',
        content: <ReportsRequested />,
      },
    ],
    [content?.availableReports?.title, content?.requestedReports?.title],
  )

  const isErrorOrInactive = isAppError || isEmpty(content) || !isActive || !isAppActive

  useScreenFinishLoadTac({
    name: 'ReportsHistory',
    screen: Routes.REPORTS_HISTORY,
    action: 'ReportsHistoryFinishLoad',
    entity: userSelectedEntity,
    shouldSend: !isAppLoading && !isErrorOrInactive,
    logLabel: 'send reports history finish tac',
  })

  if (isAppLoading) return <Loader />

  if (isErrorOrInactive) {
    const traceDetails = makeTraceDetails(!!isActive, content)

    return <ErrorHandler traceDetails={traceDetails} componentName={ReportsHistory.name} />
  }

  const { title, faqCard } = content

  return (
    <Page>
      <Header
        title={title}
        goToPath={Routes.YOUR_PERSONAL_DATA}
        showHomeIcon
        homeIconRoute={Routes.HOME}
      />
      <PageContent padding={{ all: '24px' }}>
        <StyledTabPager>
          <TabPager
            currentTabId={activeTab}
            onTabSelect={onTabSelectHandler}
            tabDefinitions={tabs}
            tabItemStyle={TabItemStyle}
            tabPanelStyle={TabPanelStyle}
            mainContainerStyle={MainContainerStyle}
          />
        </StyledTabPager>
      </PageContent>
      <Divider />
      <BottomCard>
        <PrivacyPolicyLink
          customTitle={faqCard?.title}
          customDetails={faqCard?.details}
          customButtonText={faqCard?.confirmButton}
        />
      </BottomCard>
    </Page>
  )
}
